import { ComponentType, lazy } from 'react';

const RELOAD_FLAG = 'chunkReloadAttempted';

/**
 * React.lazy that survives a stale bundle.
 *
 * Every rebuild — a dev-server recompile or a deploy — renames the code-split
 * chunks. A tab still running the previous bundle keeps asking for the old
 * filenames, the SPA host answers those 404s with index.html, and webpack tries
 * to parse the HTML as JS: "Unexpected token '<'" followed by a ChunkLoadError.
 *
 * Only fetching the new bundle can fix it, so reload once. The sessionStorage
 * flag keeps a genuinely broken chunk from looping the tab forever — the second
 * failure rethrows so the real error is visible.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      const module = await factory();
      window.sessionStorage.removeItem(RELOAD_FLAG);
      return module;
    } catch (error) {
      if (window.sessionStorage.getItem(RELOAD_FLAG) !== 'true') {
        window.sessionStorage.setItem(RELOAD_FLAG, 'true');
        window.location.reload();
        // Never settles: the reload tears the page down before React renders.
        return new Promise<{ default: T }>(() => {});
      }
      throw error;
    }
  });
}
